import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button } from 'react-bootstrap';

// GLOBAL COMPONENTS
import Sprite from 'core/tools/Sprite';

// LOCAL COMPONENTS
import Image from './Image';

// STYLES
import styles from './viewer.module.scss';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const Viewer = ({ fileName, fileUrl, mimeType, onError }) => {
  const [numPdfPages, setNumPdfPages] = useState(null);
  const [showPrint, setShowPrint] = useState(true);
  const printIframe = useRef(null);

  useEffect(() => {
    const styleElement = document.createElement('style');
    const styles = `
      #root {
        overflow: visible;
      }
      @media print {
        #root {
          overflow: hidden;
        }
      }
      @page {
        size: auto;
        margin: 0;
      }
      @page :header {
        content: none;
      }
      @page :footer {
        content: none;
      }
    `;

    styleElement.textContent = styles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, [fileUrl]);

  const handlePdfLoadSuccess = useCallback(({ numPages }) => {
    setNumPdfPages(numPages);
  }, []);

  const isImage = useMemo(() => mimeType?.startsWith('image/'), [mimeType]);
  const isPdf = useMemo(() => mimeType === 'application/pdf', [mimeType]);

  const handlePrint = useCallback(() => {
    // Use iframe as printing page has some issues with larger multipage PDFs
    printIframe.current.src = fileUrl;

    printIframe.current.onload = () => {
      printIframe.current.contentWindow.print();
    };
  }, [fileUrl]);

  if (!isImage && !isPdf) return null;

  return (
    <div className={clsx('d-flex flex-column align-items-center', styles.container)}>
      <div className={clsx('position-sticky w-100 py-4 text-center', styles.toolbar)}>
        <div>
          <a
            className="btn btn-sm btn-success d-inline-flex align-items-center justify-content-center mx-3"
            download={fileName}
            href={fileUrl}
          >
            <Sprite className="btn-sprite me-2" use="download" />
            Download
          </a>
          {showPrint && (
            <Button
              className="d-inline-flex align-items-center justify-content-center mx-3"
              variant="primary"
              size="sm"
              onClick={handlePrint}
            >
              <Sprite className="btn-sprite me-2" use="print" />
              Print
            </Button>
          )}
        </div>
        {isImage && (
          <div className="mt-4 text-gray-500 small">
            If your browser is having trouble rendering this image, please click on the Download button to view this
            file.
          </div>
        )}
      </div>
      <div className={clsx('flex-grow-1 w-100', styles.viewer)}>
        {isPdf ? (
          <Document file={fileUrl} onLoadError={onError} onLoadSuccess={handlePdfLoadSuccess}>
            {Array.from(new Array(numPdfPages), (_, i) => (
              <Page key={`page_${i + 1}`} pageNumber={i + 1} />
            ))}
          </Document>
        ) : (
          <Image fileName={fileName} fileUrl={fileUrl} onError={() => setShowPrint(false)} />
        )}
        <iframe ref={printIframe} title="Print area" className={styles.iframe} />
      </div>
    </div>
  );
};

export default Viewer;
